import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {useData} from '../../context/DataContext';
import {useLang} from '../../context/LanguageContext';
import { FaArrowLeft } from 'react-icons/fa';
import TitleOneCard from '../../components/cardsGeneration/titleOneCard';
import DateUtils from "../../utils/dateUtils";
import Footer from "../../components/Footer";

const GenerateHighOddsPage = () => {
  const navigate = useNavigate();
  const { highOddsGames } = useData()
  const { t } = useLang()
  const [selectedDays, setSelectedDays] = useState([]);
  const [numberOfGames, setNumberOfGames] = useState('');
  const [error, setError] = useState(null);

  // 1) Gather all unique days from highOddsGames
  const allDays = Array.from(
    new Set(highOddsGames?.map((g) => g.day))
  ) || [];

  // 2) Toggle day in or out of the selectedDays array
  const toggleDay = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter((d) => d !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  // 3) Handle “Generate” logic (no DB write)
  const handleGenerate = () => {
    setError(null);

    const num = parseInt(numberOfGames, 10);
    if (selectedDays.length === 0) {
      setError(t('error.atLeastADay'));
      return;
    }
    if (!num || num <= 0) {
      setError(t('error.selectValidNumberOfGames'));
      return;
    }

    // Filter highOddsGames by selected days
    const filtered = highOddsGames.filter((g) => selectedDays.includes(g.day));
    if (filtered.length < num) {
      setError(t('error.notEnoughGames').replace("$1", filtered.length).replace("$2", num));
      return;
    }

    // Shuffle and pick unique
    const uniquePicked = getRandomUniqueGames(filtered, num);
    if (uniquePicked.length < num) {
      setError(t('error.notEnoughUniqueGames'));
      return;
    }

    // Save them in localStorage so we can display them on another page
    localStorage.setItem('generatedHighOdds', JSON.stringify(uniquePicked));
    // Navigate to a results page, e.g. "/generated-high-odds"
    navigate('/generated-high-odds');
  };

  return (
    <div className="bg-[#34495e] min-h-screen p-5 relative">
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="absolute top-5 left-5 px-4 py-2 bg-[#2ecc71] rounded flex items-center space-x-2"
      >
        <FaArrowLeft />
        <span className="text-white">{t('btn.back')}</span>
      </button>

      <h1 className="text-xl text-white font-bold text-center mt-16 mb-8">
        {t('txt.generateHighOddsTicket')}
      </h1>

      {/* Days selection */}
      <div className="mb-4">
      <TitleOneCard title={t('txt.generateHighCard1')} />
        <p className="mb-2 font-semibold text-white">{t('txt.selectDays')}:</p>
        <div className="flex flex-wrap gap-2">
          {allDays.map((day) => {
            // e.g. "mar.31" => "Marți 31" if ro, or "Tuesday 31" if en
            const translatedDay = DateUtils.formatDayOnly(day, t);
            const isSelected = selectedDays.includes(day);

            return (
              <button
                key={day}
                onClick={() => toggleDay(day)}
                className={`px-3 py-1 rounded ${
                  isSelected ? 'bg-green-500 text-white' : 'bg-gray-200'
                }`}
              >
                {translatedDay}
              </button>
            );
          })}
        </div>
      </div>

      {/* Number of games */}
      <div className="mb-4">
        <p className="mb-2 font-semibold text-white">
          {t('txt.selectNumberOfGames')}:
        </p>
        <input
          type="number"
          className="border p-2 rounded w-full"
          value={numberOfGames}
          onChange={(e) => setNumberOfGames(e.target.value)}
        />
      </div>

      {error && <p className="text-red-500 mb-2">{error}</p>}

      <button
        onClick={handleGenerate}
        className="bg-blue-500 text-white font-bold py-2 px-4 rounded hover:bg-blue-600 transition"
      >
        {t('btn.generate')}
      </button>

      <TitleOneCard title={t('txt.generateCard2')} />
      <TitleOneCard title={t('txt.generateCard3')} />
      <Footer />
    </div>
  );
};

/**
 * Helper to pick `count` random unique games from `array`,
 * ensuring no duplicate team or shopCodes.
 */
function getRandomUniqueGames(array, count) {
  const shuffled = [...array];
  // Shuffle
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  const selected = [];
  const usedTeams = new Set();
  const usedShopCodes = new Set();

  for (let item of shuffled) {
    if (usedShopCodes.has(item.shopCode)) continue;
    if (usedTeams.has(item.team1) || usedTeams.has(item.team2)) continue;

    selected.push(item);
    usedShopCodes.add(item.shopCode);
    usedTeams.add(item.team1);
    usedTeams.add(item.team2);

    if (selected.length === count) break;
  }
  return selected;
}

export default GenerateHighOddsPage;
